// src/services/api.js
import axios from 'axios';
import BASE_URL from '../config';
import accessManager from '../Auth/Access';
const API_URL = `${BASE_URL}`;

// Create axios instance with interceptors for authentication
const apiClient = axios.create({
  baseURL: API_URL,
});

// Request interceptor to add authentication headers
apiClient.interceptors.request.use(
  (config) => {
    const token = accessManager.getTokens().access_token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const refreshed = await accessManager.refreshTokenWithRetry();
        if (refreshed) {
          const newToken = accessManager.getTokens().access_token;
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return apiClient(originalRequest);
        } else {
          // If refresh fails, logout user
          accessManager.logout();
          throw new Error('Authentication failed');
        }
      } catch (refreshError) {
        accessManager.logout();
        throw refreshError;
      }
    }
    
    return Promise.reject(error);
  }
);

// In api.js - Keep this version that fetches ALL groups
export const fetchGroups = async (searchQuery = '') => {
  let allGroups = [];
  let page = 1;
  let hasMore = true;
  
  while (hasMore) {
    const params = { 
      search: searchQuery,
      page: page,
      page_size: 100 // Fetch 100 at a time
    };
    
    const response = await apiClient.get('/groups/', { params });
    allGroups = [...allGroups, ...(response.data.results || [])];
    
    // Check if there are more pages
    hasMore = response.data.next !== null;
    page++;
  }
  
  return allGroups;
};

export const createMember = async (memberData) => {
  const response = await apiClient.post('/members/', memberData);
  return response.data.results || [];
};

export const transferFunds = async (fromMemberId, transferData) => {
  try {
    console.log('Transferring funds:', { fromMemberId, transferData });
    
    const response = await apiClient.post(
      `/members/${fromMemberId}/transfer_funds/`,
      transferData
    );
    
    console.log('Transfer response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error transferring funds:', error);
    throw error;
  }
};

export const getMemberById = async (id) => {
  const response = await apiClient.get(`/members/${id}/`);
  return response.data;
};

export const updateMember = async (id, memberData) => {
  const response = await apiClient.put(`/members/${id}/`, memberData);
  return response.data;
};

export const fetchMemberTransactions = async (memberId) => {
  const response = await apiClient.get(`/members/${memberId}/transactions/`);
  return response.data;
};

export const getMpesaTransactions = async () => {
  const response = await apiClient.get('/mpesa/transactions/');
  return response.data;
};

export const fetchMemberAllocations = async (memberId) => {
  const response = await apiClient.get(`/members/${memberId}/allocations/`);
  return response.data;
};

export const fetchMemberFinancialDetails = async (memberId) => {
  const response = await apiClient.get(`/members/${memberId}/financial_details/`);
  return response.data;
};

export const allocateFunds = async (memberId, allocationData) => {
  const response = await apiClient.post(
    `/members/${memberId}/allocate_funds/`,
    allocationData
  );
  console.log("Response data inside function:", response.data);
  return response.data;
};

export const migrateMemberToGroup = async (migrationData) => {
  try {
    console.log('Migrating member:', migrationData);
    
    const response = await apiClient.post('/migrate_member_to_group/', migrationData);
    
    console.log('Migration response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error migrating member:', error);
    throw error;
  }
};
// Preview loan disbursement with backend calculations
export const previewLoanDisbursement = async (loanId) => {
  try {
    const response = await apiClient.get(`/loans/${loanId}/preview_disbursement/`);
    return response.data;
  } catch (error) {
    console.error('Error previewing loan disbursement:', error);
    throw error;
  }
};

export const finalizeDeductions = async (loanId, deductionSelections) => {
  try {
    const response = await apiClient.post(`/loans/${loanId}/preview_disbursement/`, deductionSelections);
    return response.data;
  } catch (error) {
    console.error('Error finalizing deductions:', error);
    throw error;
  }
};

export const markLoanReadyForDisbursement = async (loanId, deductionSelections) => {
  try {
    const response = await apiClient.post(
      `/loans/${loanId}/mark_ready_for_disbursement/`,
      deductionSelections
    );
    return response.data;
  } catch (error) {
    console.error('Error marking loan ready for disbursement:', error);
    throw error;
  }
};

export const fetchMemberLoans = async (memberId) => {
  try {
    const response = await apiClient.get('/loans/', { params: { member_id: memberId } });
    return response.data.results || [];
  } catch (error) {
    console.error('Error fetching member loans:', error);
    throw error;
  }
};

export const submitLoanApplication = async (formData) => {
  try {
    console.log('Submitting loan application with data:', formData);
    const response = await apiClient.post('/loans/', formData);
    return response.data;
  } catch (error) {
    console.error('Error submitting loan application:', error);
    throw error;
  }
};


export const approveLoan = async (data) => {
  try {
    const response = await apiClient.post(`/loans/${data.loan_id}/approve/`, data);
    return response.data;
  } catch (error) {
    console.error('Error approving loan:', error);
    throw error;
  }
};

export const rejectLoan = async (data) => {
  try {
    const response = await apiClient.post(`/loans/${data.loan_id}/reject/`, data);
    return response.data;
  } catch (error) {
    console.error('Error rejecting loan:', error);
    throw error;
  }
};
export const fetchMembersWithPagination = async (searchQuery = '', group = 'all', page = 1, pageSize = 50) => {
  const params = { 
    search: searchQuery,
    page: page,
    page_size: pageSize
  };
  
  if (group !== 'all') {
    params.group = group;
  }
  
  const response = await apiClient.get('/members/', { params });
  return {
    members: response.data.results || [],
    count: response.data.count || 0,
    next: response.data.next,
    previous: response.data.previous
  };
};


// Update member profile (name, id_number, occupation, location)
export const updateMemberProfile = async (memberId, profileData) => {
  try {
    const response = await apiClient.patch(`/members/${memberId}/update_profile/`, profileData);
    return response.data;
  } catch (error) {
    console.error('Error updating member profile:', error);
    throw error;
  }
};

// Generate loan PDF (only requires loan_id)
export const generateLoanPDF = async (loanId) => {
  try {
    const response = await apiClient.post(`/loans/${loanId}/generate_pdf/`, {}, {
      responseType: 'blob' // Important: tells axios to expect binary data
    });
    return response.data; // This will be a Blob
  } catch (error) {
    console.error('Error generating loan PDF:', error);
    throw error;
  }
};

export const fetchFormattedLoans = async (
  cursor = null,
  status = '',
  memberId = '',
  groupId = '',
  searchQuery = '',
  startDate = '',
  endDate = ''
) => {
  try {
    const params = {};

    if (cursor) {
      params.cursor = cursor;
    }

    if (status && status !== 'all') {
      params.status = status;
    }

    if (memberId) {
      params.member_id = memberId;
    }

    if (groupId && groupId !== 'all') {
      params.group_id = groupId;
    }

    if (searchQuery && searchQuery.trim()) {
      params.search = searchQuery.trim();
    }

    if (startDate) {
      params.start_date = startDate;
    }

    if (endDate) {
      params.end_date = endDate;
    }

    const response = await apiClient.get('/loans/list_formatted/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching formatted loans:', error);
    throw error;
  }
};

export const fetchTableLoans = async (startDate = '', endDate = '', status = '') => {
  try {
    const params = {};
    if (startDate)               params.start_date = startDate;
    if (endDate)                 params.end_date   = endDate;
    if (status && status !== 'all') params.status  = status;

    const response = await apiClient.get('/loans/table_view/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching table loans:', error);
    throw error;
  }
};

export const fetchAdvanceLoansTableView = async (startDate = '', endDate = '') => {
  try {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate)   params.end_date   = endDate;

    const response = await apiClient.get('/advance-loans/table_view/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching advance loans table view:', error);
    throw error;
  }
};

export const fetchAllocationsTableView = async (startDate = '', endDate = '') => {
  try {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate)   params.end_date   = endDate;

    const response = await apiClient.get('/allocations/table_view/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching allocations table view:', error);
    throw error;
  }
};

// Preview what undoing this allocation will do (siblings, balance impact,
// and whether it's even allowed) before the admin confirms.
export const fetchAllocationUndoPreview = async (allocationId) => {
  try {
    const response = await apiClient.get(`/allocations/${allocationId}/undo_preview/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching allocation undo preview:', error);
    throw error;
  }
};

// Actually perform the undo. Admin-only — the backend returns a 403 with
// an error/message pair if the current user isn't an admin.
export const undoAllocation = async (allocationId, reason = '') => {
  try {
    const response = await apiClient.post(`/allocations/${allocationId}/undo/`, { reason });
    return response.data;
  } catch (error) {
    console.error('Error undoing allocation:', error);
    throw error;
  }
};

export const fetchLoanApprovalAssessment = async (loanId) => {
  const response = await apiClient.get('/loans/approval_assessment/', {
    params: { loan_id: loanId }
  });
  return response.data;
};
// Export loans to Excel
export const exportLoansToExcel = async (filters = {}) => {
  try {
    const params = {};
    
    // Add status filter
    if (filters.status && filters.status !== 'all') {
      params.status = filters.status;
    }
    
    // Add group filter
    if (filters.groupId && filters.groupId !== 'all') {
      params.group_id = filters.groupId;
    }
    
    // Add search query
    if (filters.searchQuery && filters.searchQuery.trim()) {
      params.search = filters.searchQuery.trim();
    }
    
    // Add date range
    if (filters.startDate) {
      params.start_date = filters.startDate;
    }
    if (filters.endDate) {
      params.end_date = filters.endDate;
    }
    
    console.log('Exporting loans with filters:', params);
    
    const response = await apiClient.get('/loans/export_to_excel/', {
      params,
      responseType: 'blob' // Important: tells axios to expect binary data
    });
    
    return response.data; // This will be a Blob
  } catch (error) {
    console.error('Error exporting loans to Excel:', error);
    throw error;
  }
};

// Updated fetchMembers to get more results by default
export const fetchMembers = async (searchQuery = '', group = 'all', pageSize = 100) => {
  const params = { 
    search: searchQuery,
    page_size: pageSize // Increase default page size
  };
  
  if (group !== 'all') {
    params.group = group;
  }
  
  const response = await apiClient.get('/members/', { params });
  return response.data.results || [];

};

export const disburseLoan = async (data) => {
  try {
    const response = await apiClient.post(`/loans/${data.loan_id}/disburse/`, data);
    return response.data;
  } catch (error) {
    console.error('Error disbursing loan:', error);
    throw error;
  }
};

export const cancelLoan = async (data) => {
  try {
    const response = await apiClient.post(`/loans/${data.loan_id}/cancel/`, data);
    return response.data;
  } catch (error) {
    console.error('Error cancelling loan:', error);
    throw error;
  }
};

export const submitAdvanceLoan = async (formData) => {
  try {
    // The issue is here - you're using formData.amount but in handleIndividualConfirm
    // you're passing principal_amount, not amount
    const dataToSubmit = {
      principal_amount: formData.principal_amount || formData.amount, // Fix: check both fields
      officer_name: formData.officer_name,
      notes: formData.notes,
      loan_type: 'advance',
      member: parseInt(formData.member || formData.member_id), // Fix: check both fields
    };

    console.log('Final dataToSubmit for individual submission:', dataToSubmit);

    const response = await apiClient.post('/advance-loans/', dataToSubmit);
    return response.data;
  } catch (error) {
    console.error('Error submitting advance loan:', error.response?.data || error);
    throw error;
  }
};

export const submitBatchAdvanceLoans = async (applications) => {
  try {
    console.log('Raw batch applications:', applications);

    const formattedApplications = applications.map(app => {
      const formattedApp = { ...app };

      if (formattedApp.member_id) {
        formattedApp.member = formattedApp.member_id;
        delete formattedApp.member_id;
      }

      if (formattedApp.amount !== undefined) {
        formattedApp.principal_amount = formattedApp.amount;
        delete formattedApp.amount;
      }

      formattedApp.loan_type = 'advance';

      return formattedApp;
    });

    console.log('Formatted batch applications:', {
      applications: formattedApplications
    });

    const response = await apiClient.post(
      '/advance-loans/batch_create/',
      { applications: formattedApplications }
    );

    console.log('Server response (batch loan):', response.data);

    return response.data;
  } catch (error) {
    console.error('Error submitting batch advance loans:', error);
    throw error;
  }
};


// Calculate advance loan interest/eligibility for a member
export const calculateAdvanceLoanInterest = async (memberId, paymentAmount) => {
  try {
    const response = await apiClient.get(
      '/advance-loans/calculate_interest/',
      { params: { member_id: memberId, payment_amount: paymentAmount } }
    );
    return response.data;
  } catch (error) {
    console.error('Error calculating advance loan interest:', error);
    throw error;
  }
};

// Get advance loan status for a member
export const getMemberAdvanceStatus = async (memberId) => {
  try {
    const url = '/advance-loans/member_status/';
    const params = { member_id: memberId };

    console.log('Sending GET request to:', url);
    console.log('With query parameters:', params);

    const response = await apiClient.get(url, { params });

    console.log('Response data:', response.data);

    return response.data;
  } catch (error) {
    console.error('Error getting member advance status:', error);
    throw error;
  }
};

// Account Offset APIs
export const previewAccountOffset = async (memberId, offsetData) => {
  try {
    const response = await apiClient.post(
      `/members/${memberId}/offset_with_savings/`,
      offsetData
    );
    return response.data;
  } catch (error) {
    console.error('Error previewing account offset:', error);
    throw error;
  }
};

export const executeAccountOffset = async (memberId, offsetData) => {
  try {
    const response = await apiClient.post(
      `/members/${memberId}/offset_with_savings/`,
      offsetData
    );
    return response.data;
  } catch (error) {
    console.error('Error executing account offset:', error);
    throw error;
  }
};